// Activation-phase poller for the Adyen card-add flow (CPS-26589, Milestone B,
// decision 6: webhook-final activation). After an AUTHORISED create/submit the
// PAN is tokenized server-side and the card flips to `Active` only once Adyen's
// webhook lands — which is asynchronous and out-of-band from the create/submit
// response. This hook bridges that gap by polling readCard(cardId) until the
// card is Active or a budget elapses.
//
// It is intentionally narrow: it owns ONLY the poll loop + the resulting phase.
// Cache invalidation (readCards / readPaymentOptions) is the CALLER's job, run
// from onActivated — this hook never touches the cache. The caller (a later
// task) flips `enabled` on when useAdyenCardAdd reaches `activating`.

import { useCallback, useEffect, useRef, useState } from 'react';

import { useQuery } from '@apollo/client';

import { READ_CARD } from 'graphql/queries/readCard';
import type {
  Card,
  Query,
  QueryReadCardArgs,
} from 'types/generatedSchemaTypes';

// Mirror of CreditCardForm's NETS readCard polling cadence.
const POLL_INTERVAL_MS = 3000;
// Activation budget. The webhook is normally near-instant, but networks and Adyen
// processing vary; ~2 min covers the slow tail while still giving the UI a bounded
// "card will appear shortly" fallback. (The card-list verifying poll is the longer
// safety net once the user leaves the add flow.)
const POLL_BUDGET_MS = 120000;

export type AdyenCardPollingPhase = 'idle' | 'polling' | 'active' | 'timedOut';

export type UseAdyenCardPollingParams = {
  cardId: string | undefined;
  // Gate: the caller turns this on only once activation has begun. While false
  // the hook is fully inert (idle, no query).
  enabled: boolean;
  // Fired exactly once when the card is observed Active, with the card readCard
  // just returned so the caller can populate UI straight from it (no extra
  // round-trip). The caller refreshes any list cache here; this hook deliberately
  // does not.
  onActivated?: (card: Card) => void;
};

export type UseAdyenCardPolling = {
  phase: AdyenCardPollingPhase;
};

export const useAdyenCardPolling = ({
  cardId,
  enabled,
  onActivated,
}: UseAdyenCardPollingParams): UseAdyenCardPolling => {
  const active = enabled && Boolean(cardId);

  const [phase, setPhase] = useState<AdyenCardPollingPhase>('idle');

  // Latest onActivated without re-arming the loop when the caller passes a new
  // closure each render.
  const onActivatedRef = useRef(onActivated);
  onActivatedRef.current = onActivated;

  // Terminal guard: once we hit active or timedOut we stop reacting to any
  // in-flight poll that resolves afterwards, and never fire onActivated twice.
  const settledRef = useRef(false);

  const finish = useCallback((next: 'active' | 'timedOut', card?: Card) => {
    if (settledRef.current) {
      return;
    }
    settledRef.current = true;
    setPhase(next);
    if (next === 'active' && card) {
      onActivatedRef.current?.(card);
    }
  }, []);

  // The cadence is Apollo's own startPolling (the same mechanism CreditCardForm
  // uses for NETS card verification), driven on/off by the active effect below.
  // network-only on every poll: the whole point is to observe the server-side,
  // webhook-driven status flip, so a cache read would defeat it. skip keeps the
  // query (and polling) fully inert while disabled / before a cardId exists.
  // Status is read in onCompleted. NOTE: this loop relies on Apollo firing
  // onCompleted on EVERY completed poll (not just the first) — true in Apollo 3.x
  // with notifyOnNetworkStatusChange + network-only, and the same contract the
  // existing CreditCardForm NETS poller depends on. Re-verify on an Apollo 4 bump.
  const { startPolling, stopPolling } = useQuery<Query, QueryReadCardArgs>(
    READ_CARD,
    {
      variables: { cardId },
      fetchPolicy: 'network-only',
      skip: !active,
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        const card = data.readCard;
        if (card?.status === 'Active') {
          stopPolling();
          finish('active', card);
        }
      },
    }
  );

  useEffect(() => {
    if (!active) {
      // Reset so a later re-enable (e.g. a retry with a fresh cardId) starts
      // clean from idle.
      settledRef.current = false;
      setPhase('idle');
      return;
    }

    settledRef.current = false;
    setPhase('polling');
    startPolling(POLL_INTERVAL_MS);

    // Independent budget: if the card never reports Active within the window we
    // stop polling and surface timedOut (the caller then renders the "card will
    // appear shortly" fallback).
    const budgetId = setTimeout(() => {
      stopPolling();
      finish('timedOut');
    }, POLL_BUDGET_MS);

    return () => {
      stopPolling();
      clearTimeout(budgetId);
    };
  }, [active, cardId, startPolling, stopPolling, finish]);

  return { phase };
};

export default useAdyenCardPolling;
